import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import DOMPurify from "dompurify";
import LegalModal from "@/components/portal/LegalModal";
// Mandanten-HTML wird vor dem Rendern bereinigt (kein <script>, keine on*-Handler); Layout-relevante Tags/Attribute bleiben erlaubt.
const CUSTOM_HTML_SANITIZE = {
  ADD_TAGS: ["style"],
  ADD_ATTR: ["target", "novalidate", "inputmode", "autocomplete", "checked", "hidden", "rel", "preserveAspectRatio", "required"],
  FORCE_BODY: true,
};

export default function CustomHtmlComingSoon({ tenant }) {
  const [status, setStatus] = useState(null);
  const [legalPage, setLegalPage] = useState(null);
  const containerRef = React.useRef(null);
  useEffect(() => {
    if (!containerRef.current) return;
    containerRef.current.innerHTML = DOMPurify.sanitize(tenant.coming_soon_custom_html || "", CUSTOM_HTML_SANITIZE);
    const root = containerRef.current;
    const anchorHandler = (e) => {
      const a = e.target.closest?.("a[href]");
      if (!a) return;
      const href = a.getAttribute("href") || "";
      const legal = /\/(impressum|datenschutz|agb)\/?(?:[?#].*)?$/i.exec(href);
      if (legal) {
        e.preventDefault();
        setLegalPage(legal[1].toLowerCase());
        return;
      }
      if (href.startsWith("#")) {
        const target = root.querySelector(href);
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };
    root.addEventListener("click", anchorHandler);
    const form = root.querySelector("form");
    if (!form) return () => root.removeEventListener("click", anchorHandler);
    const statusEl = root.querySelector("[data-lead-status]");
    const successEl = root.querySelector("[data-lead-success]");
    const submitBtn = form.querySelector('button[type="submit"]');
    const show = (state) => {
      if (statusEl) {
        if (!state) { statusEl.hidden = true; return; }
        statusEl.hidden = false;
        statusEl.className = state.loading ? "info" : state.ok ? "ok" : "err";
        statusEl.textContent = state.loading ? "Wird gesendet…" : state.msg;
      } else {
        setStatus(state);
      }
    };
    const submitHandler = async (e) => {
      e.preventDefault();
      const fd = new FormData(form);
      const payload = {};
      fd.forEach((v, k) => { payload[k] = String(v || "").trim(); });
      const email = payload.email || payload.e_mail || payload["e-mail"] || "";
      const first_name = payload.first_name || payload.vorname || "";
      const last_name = payload.last_name || payload.nachname || "";
      const zip = payload.zip || payload.plz || "";
      const city = payload.city || payload.ort || "";
      const street = payload.street || payload.strasse || "";
      const phone = payload.phone || payload.telefon || "";
      const checkboxes = Array.from(form.querySelectorAll('input[type="checkbox"]'));
      const privacyBox = form.querySelector('input[name="privacy_ack"], input[name="privacy"], input[name="datenschutz"]') || checkboxes[0];
      const newsletterBox = form.querySelector('input[name="newsletter_optin"], input[name="newsletter"]');
      const privacy_ack = !!privacyBox?.checked;
      const newsletter_optin = !!newsletterBox?.checked;
      const missing = [];
      if (!first_name && !last_name) missing.push("Name");
      if (!email || !email.includes("@")) missing.push("gültige E-Mail");
      if (!zip && !city) missing.push("PLZ/Ort");
      const streetEl = form.querySelector('input[name="street"], input[name="strasse"]');
      if (streetEl && streetEl.required && !street) missing.push("Straße & Hausnummer");
      if (missing.length) { show({ ok: false, msg: `Bitte ausfüllen: ${missing.join(", ")}.` }); return; }
      if (!privacy_ack) { show({ ok: false, msg: "Bitte bestätigen Sie die Datenschutzerklärung." }); return; }
      try {
        show({ loading: true });
        if (submitBtn) submitBtn.disabled = true;
        await api.post(`/portal/${tenant.slug}/leads`, {
          email, first_name, last_name, zip, city, street, phone, privacy_ack, newsletter_optin,
          source: "coming_soon_custom",
        });
        show(null);
        form.reset();
        if (successEl) {
          form.hidden = true;
          successEl.hidden = false;
          successEl.setAttribute("data-testid", "lead-success");
        } else {
          setStatus({ ok: true, msg: "Vielen Dank! Wir melden uns, sobald wir starten." });
        }
      } catch (err) {
        show({ ok: false, msg: err.response?.data?.detail || "Es ist ein Fehler aufgetreten." });
      } finally {
        if (submitBtn) submitBtn.disabled = false;
      }
    };
    form.addEventListener("submit", submitHandler);
    return () => {
      form.removeEventListener("submit", submitHandler);
      root.removeEventListener("click", anchorHandler);
    };
  }, [tenant.coming_soon_custom_html, tenant.slug]);

  const primary = tenant.branding?.primary_color || "#0E7C66";
  return (
    <div className="min-h-screen" data-testid="coming-soon-custom">
      <div ref={containerRef} />

      {/* Rechtliches — immer erreichbar, unabhängig vom Custom-HTML */}
      <footer className="border-t border-black/10 bg-white py-5 px-6" data-testid="coming-soon-legal-bar">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-x-6 gap-y-2 text-sm text-zinc-500">
          <button
            type="button"
            onClick={() => setLegalPage("impressum")}
            className="hover:text-zinc-900 transition-colors underline-offset-2 hover:underline font-medium"
            data-testid="legal-link-impressum"
          >
            Impressum
          </button>
          <span className="hidden sm:inline text-zinc-300">·</span>
          <button
            type="button"
            onClick={() => setLegalPage("datenschutz")}
            className="hover:text-zinc-900 transition-colors underline-offset-2 hover:underline font-medium"
            data-testid="legal-link-datenschutz"
          >
            Datenschutzerklärung
          </button>
        </div>
      </footer>

      {legalPage && (
        <LegalModal slug={tenant.slug} page={legalPage} primary={primary} onClose={() => setLegalPage(null)} />
      )}

      {status && (
        <div className={`fixed bottom-6 right-6 px-5 py-3 rounded-xl shadow-lg text-sm font-medium ${status.ok ? "bg-emerald-600 text-white" : status.loading ? "bg-zinc-800 text-white" : "bg-rose-600 text-white"}`} data-testid="lead-status-toast">
          {status.loading ? "Senden…" : status.msg}
        </div>
      )}
    </div>
  );
}
